import clsx from 'clsx';
import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import styles from './styles/modal-content.module.css';
import { ModalContext, ScrollContext } from './modal-context';
import { shallowObjectsEqual } from '../utils';
import { useCustomBreakpoints } from '../hooks';
import { BREAKPOINTS } from './constants';
/**
 * `<ModalContent>` - Обёртка для основного контента модального окна.
 * Предоставляет стили для контента, а также управляет скроллом.
 */

export const ModalContent = /*#__PURE__*/forwardRef(function ModalContent({
  children,
  className = '',
  ...props
}, ref) {
  const contentRef = useRef(null);
  const {
    variant = 'simple',
    sticky
  } = useContext(ModalContext);
  const {
    changeContentOnScroll,
    scrollTop,
    scrollBottom
  } = useContext(ScrollContext);
  const breakpoint = useCustomBreakpoints(BREAKPOINTS);
  const modalContentClass = useMemo(() => clsx(styles.content, styles[variant], sticky ? styles.sticky : '', className), [variant, sticky, className]);
  const checkScroll = useCallback(() => {
    if (changeContentOnScroll && contentRef.current && contentRef.current.parentElement) {
      const {
        parentElement
      } = contentRef.current;
      const top = parentElement.scrollTop > 0;
      const bottom = parentElement.offsetHeight + parentElement.scrollTop < parentElement.scrollHeight;
      const positionForUpdate = {
        top,
        bottom
      };
      const positionFromContext = {
        top: scrollTop,
        bottom: scrollBottom
      };

      if (!shallowObjectsEqual(positionForUpdate, positionFromContext)) {
        changeContentOnScroll(positionForUpdate);
      }
    }
  }, [changeContentOnScroll, scrollTop, scrollBottom]);
  useEffect(() => {
    checkScroll();
  }, [checkScroll, breakpoint]);
  return /*#__PURE__*/React.createElement("div", {
    className: modalContentClass,
    onScroll: checkScroll,
    ref: ref,
    "data-testid": "modal-content",
    ...props
  }, /*#__PURE__*/React.createElement("div", {
    ref: contentRef,
    className: styles.container
  }, children));
});